import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Chip,
  Popper,
  ClickAwayListener,
  Grow,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useDebounce } from '../../Hooks/useDebounce';
import { useAPI } from '../../Hooks/useAPIs';
import { useInfiniteQuery } from '@tanstack/react-query';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const FilterDropdown = ({
  field,
  label,
  value,
  onChange,
  apiBaseUrl = '/api/filters',
  customEndpoint = null,
  multiple = true,
  icon: Icon,
  disabled = false,
  dateFilters = null,
  otherFilters = null,
  fieldMapping = null,
  staticOptions = null,
  getOptionLabel = null,
  isOptionEqualToValue = null,
  getOptionDisplayText = null,
  getOptionSecondary = null,
  isLoading: externalLoading = false,
  disableSearch = false,
  disablePopperFlip = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [buttonWidth, setButtonWidth] = useState(0);
  const debouncedSearch = useDebounce(searchTerm, 300);
  const { apiCall } = useAPI();

  const buttonRef = useRef(null);
  const observerTarget = useRef(null);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (buttonRef.current) {
        setButtonWidth(buttonRef.current.offsetWidth);
      }
    };
    
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, [isOpen]);

  const selectedValues = multiple
    ? (Array.isArray(value) ? value : [])
    : value ? [value] : [];

  const getLabel = useCallback((opt) => {
    if (getOptionLabel) return getOptionLabel(opt);
    if (typeof opt === 'object' && opt) return opt.displayText || opt.label || opt.value || String(opt);
    return String(opt);
  }, [getOptionLabel]);

  const previousOptionsRef = useRef([]);
  const hasMoreMapRef = useRef(new Map());

  const prevStaticOptionsRef = useRef(staticOptions);
  React.useEffect(() => {
    if (prevStaticOptionsRef.current !== staticOptions) {
      hasMoreMapRef.current.clear();
      previousOptionsRef.current = staticOptions || [];
      prevStaticOptionsRef.current = staticOptions;
    }
  }, [staticOptions]);

  const buildQueryParams = useCallback(
    (shouldSearch, searchTerm, pageNum, includeField = true) => {
      const params = new URLSearchParams({
        limit: '15',
        offset: ((pageNum - 1) * 15).toString(),
      });
      
      if (includeField) {
        params.append('field', field);
      }
      
      if (shouldSearch && searchTerm) {
        params.append('q', searchTerm);
      }

      if (dateFilters) {
        const { start_datetime, end_datetime, dateType, createIsoStringWithTimezone } = dateFilters;

        if (start_datetime && createIsoStringWithTimezone) {
          const iso = createIsoStringWithTimezone(start_datetime);
          if (iso) params.append('start_date', iso);
        }
        if (end_datetime && createIsoStringWithTimezone) {
          const iso = createIsoStringWithTimezone(end_datetime);
          if (iso) params.append('end_date', iso);
        }
        if (dateType) params.append('use_datetime_type', dateType);
      }

      if (otherFilters && fieldMapping) {
        Object.entries(otherFilters).forEach(([key, val]) => {
          if (key === field || !Array.isArray(val) || val.length === 0) return;
          const param = fieldMapping[key] || key;
          params.append(param, val.join(','));
        });
      }

      return params;
    },
    [field, dateFilters, otherFilters, fieldMapping]
  );

  const shouldSearchOnServer = useMemo(() => {
    if (disableSearch) return false;
    if (!debouncedSearch.trim()) return false;

    if (customEndpoint) return true;

    const lower = debouncedSearch.toLowerCase();
    const currentOptions = staticOptions || previousOptionsRef.current;

    if (currentOptions.length === 0) return true;

    const hasMore = hasMoreMapRef.current.get(lower);
    if (hasMore === false) return false;

    const filtered = currentOptions.filter(opt => getLabel(opt).toLowerCase().includes(lower));

    if (filtered.length > 0) {
      hasMoreMapRef.current.set(lower, false);
      return false;
    }

    return hasMore !== false;
  }, [debouncedSearch, staticOptions, getLabel, disableSearch, customEndpoint]);

  const lastServerSearchRef = useRef('');
  const effectiveSearchTerm = useMemo(() => {
    if (disableSearch || !debouncedSearch.trim()) {
      lastServerSearchRef.current = '';
      return '';
    }

    if (customEndpoint) {
      lastServerSearchRef.current = debouncedSearch;
      return debouncedSearch;
    }

    const isRefinement = lastServerSearchRef.current && debouncedSearch.startsWith(lastServerSearchRef.current);

    if (shouldSearchOnServer || !isRefinement) {
      lastServerSearchRef.current = debouncedSearch;
      return debouncedSearch;
    }

    return lastServerSearchRef.current;
  }, [debouncedSearch, shouldSearchOnServer, disableSearch, customEndpoint]);

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['autocomplete', field, effectiveSearchTerm, dateFilters?.start_datetime, dateFilters?.end_datetime, dateFilters?.dateType, JSON.stringify(otherFilters), customEndpoint],
    queryFn: async ({ pageParam = 1 }) => {
      if (!customEndpoint && debouncedSearch.trim() && !shouldSearchOnServer) {
        const currentOpts = previousOptionsRef.current;
        if (currentOpts.length > 0) {
          const lower = debouncedSearch.toLowerCase();
          const filtered = currentOpts.filter(opt => getLabel(opt).toLowerCase().includes(lower));
          if (filtered.length > 0) {
            hasMoreMapRef.current.set(lower, false);
            return {
              results: [],
              hasNextPage: false,
              page: pageParam,
              hasMore: false,
            };
          }
        }
      }
      
      const params = buildQueryParams(effectiveSearchTerm !== '', effectiveSearchTerm, pageParam, true);      
      const separator = customEndpoint?.includes('?') ? '&' : '?';
      const endpoint = customEndpoint 
        ? `${customEndpoint}${separator}${params}`
        : `${apiBaseUrl}/autocomplete/?${params}`;
      const res = await apiCall('GET', endpoint);
      
      let results = [];
      let hasMore = true;
      
      if (customEndpoint) {
        if (res.data && res.data[field]) {
          results = Array.isArray(res.data[field]) ? res.data[field] : [];
        } else if (res.data && res.data.results) {
          results = res.data.results;
        } else if (Array.isArray(res.data)) {
          results = res.data;
        } else {
          results = [];
        }
        if (res.data && typeof res.data.has_more === 'boolean') {
          hasMore = res.data.has_more;
        } else if (res.data && typeof res.data.hasMore === 'boolean') {
          hasMore = res.data.hasMore;
        }
      } else {
        results = res.data.results || [];
        if (typeof res.data.has_more === 'boolean') {
          hasMore = res.data.has_more;
        } else if (typeof res.data.hasMore === 'boolean') {
          hasMore = res.data.hasMore;
        }
      }
      
      if (shouldSearchOnServer && debouncedSearch) {
        hasMoreMapRef.current.set(debouncedSearch.toLowerCase(), hasMore);
      }
      
      const hasNext = typeof hasMore === 'boolean' ? hasMore : results.length === 15;
      
      return {
        results,
        hasNextPage: hasNext,
        page: pageParam,
        hasMore,
      };
    },
    getNextPageParam: (lastPage) => lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    enabled: isOpen && !staticOptions,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });

  const options = useMemo(() => {
    if (staticOptions) {
      previousOptionsRef.current = staticOptions;
      return staticOptions;
    }
    const opts = data?.pages?.flatMap(p => p.results) || [];
    previousOptionsRef.current = opts;
    return opts;
  }, [data, staticOptions]);

  const loading = staticOptions ? externalLoading : (isLoading || isFetching);

  const isEqual = useCallback((a, b) => {
    if (isOptionEqualToValue) return isOptionEqualToValue(a, b);
    return a === b || JSON.stringify(a) === JSON.stringify(b);
  }, [isOptionEqualToValue]);

  const getDisplayText = useCallback((opt) => {
    if (getOptionDisplayText) return getOptionDisplayText(opt);
    return getLabel(opt);
  }, [getOptionDisplayText, getLabel]);

  const filteredOptions = useMemo(() => {
    if (disableSearch) return options;
    if (!debouncedSearch.trim()) return options;
    if (customEndpoint) return options;
    const lower = debouncedSearch.toLowerCase();
    return options.filter(opt => getLabel(opt).toLowerCase().includes(lower));
  }, [options, debouncedSearch, getLabel, disableSearch, customEndpoint]);

  React.useEffect(() => {
    if (!isOpen || !hasNextPage || isFetchingNextPage || !observerTarget.current) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) fetchNextPage();
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSelect = (opt) => {
    if (multiple) {
      const newVals = selectedValues.some(v => isEqual(v, opt))
        ? selectedValues.filter(v => !isEqual(v, opt))
        : [...selectedValues, opt];
      onChange(newVals);
    } else {
      onChange(opt);
      setIsOpen(false);
    }
  };

  const handleRemoveChip = (chip) => {
    onChange(selectedValues.filter(v => !isEqual(v, chip)));
  };

  const displayText = multiple
    ? selectedValues.length === 0
      ? `Select ${label}...`
      : selectedValues.length === 1
        ? getDisplayText(selectedValues[0])
        : `${selectedValues.length} selected`
    : value ? getDisplayText(value) : `Select ${label}...`;

  const dropdownWidth = useMemo(() => {
    if (!buttonWidth) return '100%';
    return buttonWidth;
  }, [buttonWidth]);

  const dropdownMaxHeight = useMemo(() => {
    if (windowWidth >= 1280) return '450px';
    if (windowWidth >= 960) return '400px';
    if (windowWidth >= 600) return '350px';
    return '300px';
  }, [windowWidth]);

  return (
    <Box sx={{ width: '100%', mb: 0.5 }}>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          <Typography variant="body2" fontWeight={700}>
            {label}
          </Typography>
        </Box>
      )}

      {multiple && selectedValues.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
          {selectedValues.slice(0, 2).map((v, i) => (
            <Chip
              key={i}
              label={getDisplayText(v)}
              size="small"
              onDelete={() => handleRemoveChip(v)}
              sx={{ height: 24, fontSize: '0.75rem' }}
            />
          ))}
          {selectedValues.length > 2 && (
            <Chip label={`+${selectedValues.length - 2} more`} size="small" />
          )}
        </Box>
      )}

      <Button
        ref={buttonRef}
        onClick={() => {
          setIsOpen(prev => {
            if (prev) {
              setSearchTerm('');
              lastServerSearchRef.current = '';
            }
            return !prev;
          });
        }}
        variant="outlined"
        fullWidth
        disabled={disabled}
        sx={{
          justifyContent: 'space-between',
          textTransform: 'none',
          color: selectedValues.length > 0 || value ? 'text.primary' : 'text.secondary',
          borderColor: 'divider',
          minHeight: 36,
          py: 0.5,
          '&:hover': { borderColor: disabled ? 'divider' : 'primary.main' },
        }}
        endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        <Typography variant="body2" noWrap sx={{ flex: 1, textAlign: 'left' }}>
          {displayText}
        </Typography>
      </Button>

      <Popper
        open={isOpen}
        anchorEl={buttonRef.current}
        transition
        disablePortal={false}
        placement="bottom-start"
        modifiers={[
          { name: 'flip', enabled: !disablePopperFlip },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: false,
              altBoundary: false,
              tether: false,
              rootBoundary: 'viewport',
              padding: 8,
            },
          },
          { name: 'offset', options: { offset: [0, 8] } },
        ]}
        sx={{ 
          zIndex: 1300,
          width: dropdownWidth,
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement.includes('bottom') ? 'left top' : 'left bottom' }}
          >
            <Paper elevation={8} sx={{ width: '100%', maxHeight: dropdownMaxHeight, display: 'flex', flexDirection: 'column' }}>
              <ClickAwayListener onClickAway={() => { setIsOpen(false); setSearchTerm(''); lastServerSearchRef.current = ''; }}>
                <div>
                  {/* Search */}
                  {!disableSearch && (
                    <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={`Search ${label.toLowerCase()}...`}
                        autoFocus
                        onKeyDown={e => e.stopPropagation()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}

                  {/* List */}
                  <Box sx={{ overflow: 'auto', maxHeight: `calc(${dropdownMaxHeight} - 64px)` }}>
                    {loading && (options.length === 0 || isFetching) && filteredOptions.length === 0 ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" sx={{ ml: 1 }}>Loading...</Typography>
                      </Box>
                    ) : filteredOptions.length === 0 && !isFetching ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No results found
                        </Typography>
                      </Box>
                    ) : filteredOptions.length === 0 && isFetching ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" sx={{ ml: 1 }}>Searching...</Typography>
                      </Box>
                    ) : (
                      <List sx={{ p: 0 }}>
                        {filteredOptions
                          .filter(opt => opt != null)
                          .map((opt, idx) => {
                            const selected = selectedValues.some(v => isEqual(v, opt));
                            const key = typeof opt === 'object' && opt !== null ? opt.id || opt.value || JSON.stringify(opt) : opt;
                            return (
                              <ListItem key={`${key}-${idx}`} disablePadding>
                                <ListItemButton onClick={() => handleSelect(opt)}>
                                  {multiple && (
                                    <Checkbox
                                      icon={icon}
                                      checkedIcon={checkedIcon}
                                      checked={selected}
                                      sx={{ mr: 1, p: 0.5 }}
                                    />
                                  )}
                                  <ListItemText 
                                    primary={getLabel(opt)} 
                                    secondary={getOptionSecondary ? getOptionSecondary(opt) : undefined}
                                    primaryTypographyProps={getOptionSecondary ? { fontWeight: 500, fontSize: '0.875rem' } : undefined}
                                    secondaryTypographyProps={getOptionSecondary ? { variant: 'caption' } : undefined}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                      </List>
                    )}

                    {hasNextPage && (
                      <Box ref={observerTarget} sx={{ py: 1, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => fetchNextPage()}
                          disabled={isFetchingNextPage}
                          sx={{ 
                            minWidth: 120,
                            textTransform: 'none'
                          }}
                        >
                          {isFetchingNextPage ? (
                            <>
                              <CircularProgress size={14} sx={{ mr: 1 }} />
                              Loading...
                            </>
                          ) : (
                            'Load More'
                          )}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default FilterDropdown;